<template>
  <!-- Anonymous state -->
  <div
    v-if="selectedData.isAnonymous"
    class="cf-anonymous"
  >
    <i class="fa-light fa-user-secret" />
    <span>{{ t('dataExplorer.anonymous') }}</span>
  </div>

  <!-- Contact file -->
  <div
    v-else
    class="cf"
  >
    <!-- RGPD (SC-3851) : dialogue de confirmation de l'effacement définitif -->
    <GdprEraseAction
      ref="gdprEraseRef"
      @confirm="onGdprEraseConfirm"
    />

    <!-- ===== HERO HEADER ===== -->
    <div class="cf-hero">
      <div class="cf-hero__left">
        <div class="cf-hero__avatar">
          <span>{{ initials }}</span>
        </div>
        <div class="cf-hero__identity">
          <h2 class="cf-hero__name">
            <template v-if="hasName">
              <span class="capitalize">{{ selectedData.generalInformation.data.firstName }}</span>
              <span class="uppercase">{{ selectedData.generalInformation.data.lastName }}</span>
            </template>
            <span
              v-else
              class="cf-hero__no-name"
            >{{ t('dataExplorer.contactFile.fields.noName') }}</span>
          </h2>
          <div class="cf-hero__meta">
            <span
              v-if="isNumber(selectedData.generalInformation.data.idCustomerShop)"
              class="cf-hero__id"
            >#{{ selectedData.generalInformation.data.idCustomerShop }}</span>
            <span
              v-if="hasAccount"
              class="cf-hero__badge"
              :class="isAccountActive ? 'cf-hero__badge--active' : 'cf-hero__badge--inactive'"
            >
              <i :class="isAccountActive ? 'fas fa-check-circle' : 'fas fa-times-circle'" />
              {{ isAccountActive ? t('dataExplorer.contactFile.fields.account.active.badge.active') : t('dataExplorer.contactFile.fields.account.active.badge.inactive') }}
            </span>
            <span
              v-for="source in contactSources"
              :key="source.id"
              class="cf-hero__source-badge"
            >
              <i class="far fa-database" />
              {{ source.label }}
            </span>
          </div>
        </div>
      </div>
      <div class="cf-hero__dates">
        <!-- RGPD (SC-3851) : effacement définitif des données du contact -->
        <SpmButton
          v-if="canGdprErase"
          :label="t('dataExplorer.contactFile.gdprErase.button')"
          icon="far fa-user-slash"
          class-style="p-button p-button-text p-button-danger p-button-sm"
          @click="openGdprErase"
        />
        <div class="cf-hero__date">
          <i class="fa-light fa-calendar-plus" />
          <span class="cf-hero__date-label">{{ t('dataExplorer.contactFile.fields.dates.createdOn') }}</span>
          <span class="cf-hero__date-value">{{ formatDate(selectedData.generalInformation.data.dateCreation, 'YYYY-MM-DD HH:mm:ss', 'DD/MM/YYYY') }}</span>
        </div>
        <span class="cf-hero__date-sep" />
        <div class="cf-hero__date">
          <i class="fa-light fa-calendar-edit" />
          <span class="cf-hero__date-label">{{ t('dataExplorer.contactFile.fields.dates.modifiedOn') }}</span>
          <span class="cf-hero__date-value">{{ formatDate(selectedData.generalInformation.data.dateModification, 'YYYY-MM-DD HH:mm:ss', 'DD/MM/YYYY') }}</span>
        </div>
      </div>
    </div>

    <!-- RGPD (SC-3851) : le compte a été supprimé sur la boutique. Sans cette
         explication, l'écran laisse croire que la suppression n'a pas été reçue. -->
    <div
      v-if="isAccountDeletedOnShop"
      class="cf-deleted-notice"
    >
      <i class="far fa-circle-info" />
      <span>{{ t('dataExplorer.contactFile.gdprErase.deletedOnShop') }}</span>
    </div>

    <!-- ===== MAIN 2-COL LAYOUT ===== -->
    <div class="cf-layout">
      <!-- ===== LEFT COLUMN: Contact info ===== -->
      <div class="cf-left">
        <!-- Quick contact -->
        <div class="cf-card">
          <div class="cf-card__header">
            <i class="fa-light fa-address-book" />
            <h3>{{ t('dataExplorer.contactFile.fields.sections.personalInfo') }}</h3>
          </div>
          <div class="cf-card__body">
            <div class="cf-info-row">
              <div class="cf-info-row__icon">
                <i class="fa-light fa-envelope" />
              </div>
              <div class="cf-info-row__content">
                <span class="cf-info-row__label">{{ t('dataExplorer.contactFile.fields.email') }}</span>
                <span class="cf-info-row__value">{{ selectedData.generalInformation.data.email || '-' }}</span>
              </div>
            </div>
            <div class="cf-info-row">
              <div class="cf-info-row__icon">
                <i class="fa-light fa-phone" />
              </div>
              <div class="cf-info-row__content">
                <span class="cf-info-row__label">{{ t('dataExplorer.contactFile.fields.primaryPhoneNumber') }}</span>
                <span class="cf-info-row__value">{{ phoneNumber || '-' }}</span>
              </div>
            </div>
            <div class="cf-info-row">
              <div class="cf-info-row__icon">
                <i class="fa-light fa-cake-candles" />
              </div>
              <div class="cf-info-row__content">
                <span class="cf-info-row__label">{{ t('dataExplorer.contactFile.fields.birthday') }}</span>
                <span class="cf-info-row__value">
                  <template v-if="selectedData.generalInformation.data.birthday">
                    {{ formatDate(selectedData.generalInformation.data.birthday, 'YYYY-MM-DD', 'DD/MM/YYYY') }}
                  </template>
                  <span
                    v-else
                    class="cf-info-row__empty"
                  >-</span>
                </span>
              </div>
            </div>
            <div class="cf-info-row">
              <div class="cf-info-row__icon">
                <i :class="gender.icon" />
              </div>
              <div class="cf-info-row__content">
                <span class="cf-info-row__label">{{ t('dataExplorer.contactFile.fields.gender.title') }}</span>
                <span class="cf-info-row__value">{{ gender.label }}</span>
              </div>
            </div>
            <div class="cf-info-row">
              <div class="cf-info-row__icon">
                <i class="fa-light fa-globe" />
              </div>
              <div class="cf-info-row__content">
                <span class="cf-info-row__label">{{ t('dataExplorer.contactFile.fields.country') }}</span>
                <span class="cf-info-row__value">
                  <Flag
                    v-if="selectedData.generalInformation.data.country"
                    :country="selectedData.generalInformation.data.country"
                  />
                  <span
                    v-else
                    class="cf-info-row__empty"
                  >-</span>
                </span>
              </div>
            </div>
            <div class="cf-info-row">
              <div class="cf-info-row__icon">
                <i class="fa-light fa-language" />
              </div>
              <div class="cf-info-row__content">
                <span class="cf-info-row__label">{{ t('dataExplorer.contactFile.fields.language') }}</span>
                <span class="cf-info-row__value">
                  <LanguageIcons
                    v-if="selectedData.generalInformation.data.lang"
                    :translation="t"
                    :language="selectedData.generalInformation.data.lang"
                  />
                  <span
                    v-else
                    class="cf-info-row__empty"
                  >-</span>
                </span>
              </div>
            </div>
            <div
              v-if="hasAccount && selectedData.generalInformation.data.date"
              class="cf-info-row"
            >
              <div class="cf-info-row__icon">
                <i class="fa-light fa-user-plus" />
              </div>
              <div class="cf-info-row__content">
                <span class="cf-info-row__label">{{ t('dataExplorer.contactFile.fields.account.creationDate') }}</span>
                <span class="cf-info-row__value">{{ formatDate(selectedData.generalInformation.data.date, 'YYYY-MM-DD HH:mm:ss', 'DD/MM/YYYY') }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Points de vente et identifiants : une fiche client par point de vente -->
        <div class="cf-card">
          <div class="cf-card__header">
            <i class="fa-light fa-store" />
            <h3>{{ t('dataExplorer.contactFile.fields.sections.sources') }}</h3>
            <SpmBadge
              v-if="sourceProfilesCount"
              :label="String(sourceProfilesCount)"
              :colors="{ bg: '#eef2ff', color: '#4338ca' }"
              size="sm"
              variant="soft"
              class="ml-auto"
            />
          </div>
          <div class="cf-card__body">
            <ContactSourceProfiles
              v-if="selectedData.generalInformation.data.idShopContact"
              :id-shop-contact="Number(selectedData.generalInformation.data.idShopContact)"
              @loaded="(count) => { sourceProfilesCount = count; }"
            />
          </div>
        </div>

        <!-- Communication channels -->
        <div class="cf-card">
          <div class="cf-card__header">
            <i class="fa-light fa-tower-broadcast" />
            <h3>{{ t('dataExplorer.contactFile.fields.sections.communication') }}</h3>
          </div>
          <div class="cf-card__body cf-card__body--no-pad">
            <div
              class="cf-channel"
              :class="'cf-channel--' + channelStatuses.email.cssClass"
            >
              <div class="cf-channel__icon">
                <i class="fa-light fa-envelope" />
              </div>
              <span class="cf-channel__name">Email</span>
              <span class="cf-channel__status">{{ channelStatuses.email.label }}</span>
            </div>
            <div
              class="cf-channel"
              :class="'cf-channel--' + channelStatuses.sms.cssClass"
            >
              <div class="cf-channel__icon">
                <i class="fa-light fa-mobile-alt" />
              </div>
              <span class="cf-channel__name">SMS</span>
              <span class="cf-channel__status">{{ channelStatuses.sms.label }}</span>
            </div>
            <div
              class="cf-channel"
              :class="'cf-channel--' + channelStatuses.push.cssClass"
            >
              <div class="cf-channel__icon">
                <i class="fa-light fa-bell" />
              </div>
              <span class="cf-channel__name">Push</span>
              <span class="cf-channel__status">{{ channelStatuses.push.label }}</span>
            </div>
          </div>
        </div>

        <!-- Tags -->
        <div class="cf-card">
          <div class="cf-card__header">
            <i class="fa-light fa-tags" />
            <h3>{{ t('dataExplorer.contactFile.fields.tags') }}</h3>
            <SpmBadge
              v-if="selectedData.generalInformation.data.tags.length"
              :label="String(selectedData.generalInformation.data.tags.length)"
              :colors="{ bg: '#eef2ff', color: '#4338ca' }"
              size="sm"
              variant="soft"
              class="ml-auto"
            />
          </div>
          <div class="cf-card__body">
            <div
              v-if="selectedData.generalInformation.data.tags.length"
              class="cf-tags"
            >
              <span
                v-for="tag in selectedData.generalInformation.data.tags"
                :key="tag"
                class="cf-tag-chip"
              >
                <i class="fa-light fa-tag" />
                <span class="cf-tag-chip__label">{{ tag }}</span>
              </span>
            </div>
            <div
              v-else
              class="cf-tags__empty"
            >
              <i class="fa-light fa-tag" />
              <span>{{ t('dataExplorer.contactFile.fields.noTags') }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== RIGHT COLUMN: Activity & data ===== -->
      <div class="cf-right">
        <div class="cf-card cf-card--full">
          <div class="cf-card__header cf-card__header--no-border">
            <i class="fa-light fa-chart-line" />
            <h3>{{ t('dataExplorer.contactFile.fields.sections.activity') }}</h3>
          </div>
          <TabView>
            <!-- Addresses Tab -->
            <TabPanel>
              <template #header>
                <i class="fa-light fa-location-dot mr-2" />
                <span>{{ t('dataExplorer.contactFile.fields.tabs.addresses') }}</span>
                <SpmBadge
                  v-if="selectedData.addresses.dataLoaded"
                  :label="String(selectedData.addresses.data.length)"
                  :colors="{ bg: '#f0fdf4', color: '#15803d' }"
                  size="sm"
                  variant="soft"
                  class="ml-2"
                />
              </template>
              <div
                id="addresses-grid-container"
                class="addresses-grid-wrapper"
              >
                <List
                  v-if="selectedData.generalInformation.data.idCustomerShop"
                  :title="t('dataExplorer.contactFile.fields.addresses.title')"
                  :list="selectedData.addresses.data"
                  :no-column="true"
                  :retrieve-data="getContactAddressesNest"
                  :retrieve-data-arguments="[selectedData.generalInformation.data.idShopCustomer]"
                  :data-loaded="selectedData.addresses.dataLoaded"
                  key-name="idShopCustomerAddress"
                  @loaded="handleRetrieveAddresses"
                >
                  <template #content="{ item }">
                    <AddressItem :address="item" />
                  </template>
                </List>
              </div>
            </TabPanel>

            <!-- Orders Tab -->
            <TabPanel>
              <template #header>
                <i class="fa-light fa-shopping-cart mr-2" />
                <span>{{ t('dataExplorer.contactFile.fields.tabs.orders') }}</span>
                <SpmBadge
                  v-if="selectedData.orders.dataLoaded"
                  :label="String(selectedData.orders.total || 0)"
                  :colors="{ bg: '#f0fdf4', color: '#15803d' }"
                  size="sm"
                  variant="soft"
                  class="ml-2"
                />
              </template>
              <List
                v-if="selectedData.generalInformation.data.idCustomerShop"
                :title="t('dataExplorer.contactFile.fields.orders')"
                :list="selectedData.orders.data"
                :retrieve-data="getContactOrdersNest"
                :retrieve-data-arguments="[selectedData.generalInformation.data.idShopContact]"
                :data-loaded="selectedData.orders.dataLoaded"
                :no-column="true"
                :total="selectedData.orders.total"
                :current-page="selectedData.orders.currentPage"
                key-name="idShopOrder"
                @loaded="handleRetrieveOrders"
                @update-page="handleOrdersNextPage"
              >
                <template #content="{ item }">
                  <OrderItem
                    :order="item"
                    :source-label="getSourceLabel(item.dataSourceId)"
                    key-name="orders"
                  />
                </template>
              </List>
            </TabPanel>

            <!-- Carts Tab -->
            <TabPanel>
              <template #header>
                <i class="fa-light fa-shopping-basket mr-2" />
                <span>{{ t('dataExplorer.contactFile.fields.tabs.carts') }}</span>
                <SpmBadge
                  v-if="selectedData.carts.dataLoaded"
                  :label="String(selectedData.carts.total || 0)"
                  :colors="{ bg: '#f0fdf4', color: '#15803d' }"
                  size="sm"
                  variant="soft"
                  class="ml-2"
                />
              </template>
              <List
                v-if="selectedData.generalInformation.data.idCustomerShop"
                :title="t('dataExplorer.contactFile.fields.carts')"
                :list="selectedData.carts.data"
                :retrieve-data="getContactCartsNest"
                :retrieve-data-arguments="[selectedData.generalInformation.data.idShopContact]"
                :data-loaded="selectedData.carts.dataLoaded"
                :no-column="true"
                :total="selectedData.carts.total"
                :current-page="selectedData.carts.currentPage"
                key-name="idShopCart"
                @loaded="handleRetrieveCarts"
                @update-page="handleCartsNextPage"
              >
                <template #content="{ item }">
                  <CartItem
                    :cart="item"
                    :source-label="getSourceLabel(item.dataSourceId)"
                    key-name="carts"
                  />
                </template>
              </List>
            </TabPanel>

            <!-- Groups Tab -->
            <TabPanel>
              <template #header>
                <i class="fa-light fa-users mr-2" />
                <span>{{ t('dataExplorer.contactFile.fields.tabs.groups') }}</span>
                <SpmBadge
                  v-if="selectedData.groups.dataLoaded"
                  :label="String(selectedData.groups.data.length)"
                  :colors="{ bg: '#f0fdf4', color: '#15803d' }"
                  size="sm"
                  variant="soft"
                  class="ml-2"
                />
              </template>
              <List
                v-if="selectedData.generalInformation.data.idCustomerShop"
                :title="t('dataExplorer.contactFile.fields.groups')"
                :list="selectedData.groups.data"
                :retrieve-data="getContactGroupsNest"
                :retrieve-data-arguments="[selectedData.generalInformation.data.idShopCustomer]"
                :data-loaded="selectedData.groups.dataLoaded"
                key-name="idGroup"
                @loaded="handleRetrieveGroups"
              >
                <template #content="{ item }">
                  <div class="cf-list-item">
                    <i class="fa-light fa-users cf-list-item__icon" />
                    <span>{{ item.name }}</span>
                  </div>
                </template>
              </List>
            </TabPanel>

            <!-- Lists Tab -->
            <TabPanel>
              <template #header>
                <i class="fa-light fa-list mr-2" />
                <span>{{ t('dataExplorer.contactFile.fields.tabs.lists') }}</span>
                <SpmBadge
                  v-if="selectedData.shops.dataLoaded"
                  :label="String(selectedData.shops.data.length)"
                  :colors="{ bg: '#f0fdf4', color: '#15803d' }"
                  size="sm"
                  variant="soft"
                  class="ml-2"
                />
              </template>
              <List
                :title="t('dataExplorer.contactFile.fields.shopsCustomersLists')"
                :list="selectedData.shops.data"
                :retrieve-data="getContactListNest"
                :retrieve-data-arguments="[selectedData.generalInformation.data.idShopContact, locale]"
                :data-loaded="selectedData.shops.dataLoaded"
                :no-column="true"
                key-name="idList"
                @loaded="handleRetrieveShops"
              >
                <template #content="{ item }">
                  <div class="cf-list-item">
                    <i class="fa-light fa-list cf-list-item__icon" />
                    <span>{{ item.name }}</span>
                  </div>
                </template>
              </List>
            </TabPanel>

            <!-- Custom data Tab -->
            <TabPanel>
              <template #header>
                <i class="fa-light fa-database mr-2" />
                <span>{{ t('dataExplorer.contactFile.fields.tabs.customData') }}</span>
                <SpmBadge
                  v-if="selectedData.customData.dataLoaded"
                  :label="String(selectedData.customData.data.length)"
                  :colors="{ bg: '#f0fdf4', color: '#15803d' }"
                  size="sm"
                  variant="soft"
                  class="ml-2"
                />
              </template>
              <List
                :title="t('dataExplorer.contactFile.fields.customData.title')"
                :list="selectedData.customData.data"
                :retrieve-data="getContactCustomDefinitionsNest"
                :retrieve-data-arguments="[selectedData.generalInformation.data.idShopContact]"
                :data-loaded="selectedData.customData.dataLoaded"
                :no-column="true"
                key-name="idDefinition"
                @loaded="handleRetrieveCustomDefinitions"
              >
                <template #content="{ item }">
                  <div class="cf-list-item cf-list-item--with-action">
                    <div class="cf-list-item__left">
                      <i class="fa-light fa-database cf-list-item__icon" />
                      <div class="cf-list-item__meta">
                        <span>{{ item.schemaName }}</span>
                        <small v-if="item.description">{{ item.description }}</small>
                      </div>
                    </div>
                    <SpmButton
                      v-tooltip.top="t('dataExplorer.contactFile.fields.customData.openDetailsBtn')"
                      icon="fa-light fa-circle-info"
                      class-style="p-button-outlined p-button-sm"
                      @click="openCustomDefinitionDetails(item)"
                    />
                  </div>
                </template>
              </List>
            </TabPanel>
          </TabView>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed, defineComponent, ref, onBeforeMount, watch,
} from 'vue';

import Tooltip from 'primevue/tooltip';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';

import SpmBadge from '@/components/table-rest-api/SpmBadge.vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';

import Flag from '@/components/table/renderers/Flag.vue';
import LanguageIcons from '@/components/table/renderers/LanguageIcons.vue';
import List from '@/components/data-explorer/fields/List.vue';
import AddressItem from '@/components/data-explorer/fields/AddressItem.vue';
import OrderItem from '@/components/data-explorer/fields/OrderItem.vue';
import CartItem from '@/components/data-explorer/fields/CartItem.vue';
import ContactSourceProfiles from '@/components/data-explorer/fields/ContactSourceProfiles.vue';

import {
  getContactAddressesNest,
  getContactCustomDefinitionsNest,
  getContactGroupsNest,
  getContactListNest,
  getContactOrdersNest,
  getContactCartsNest,
} from '@/composables/data-explorer/DataExplorer';

import { fetchDataSources } from '@/composables/segmentations/helpers/fetchData';
import getChannelSubscriptionStatus, {
  ChannelSubscriptionStatus,
  SubscriptionChannel,
} from '@/composables/contacts/channelSubscription';

import { useStore } from '@/store';

import { formatDate } from '@/helpers/Date';

import {
  ContactAddress,
  ContactCustomDefinition,
  ContactGroup,
  ContactReject,
  Order,
  Cart,
} from '@/types/data-explorer-types';

import { useI18n } from 'vue-i18n';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

import { NestApiType, nestCall } from '@/composables/nestApi';
import { showToastError, showToastSuccess } from '@/helpers';
import GdprEraseAction from '@/views/mes-listes/customers/ListsContactsActions/GdprEraseAction.vue';
import type { GdprEraseResult } from '@/views/mes-listes/customers/ListsContactsActions/types';

/** Dialogue d'effacement RGPD, piloté par sa méthode `open` exposée (SC-3851). */
interface GdprEraseDialog {
  open?: (payload: { context: string; selectionInfo: null; rowId: null }) => void;
}

export default defineComponent({
  name: 'ContactFile',

  directives: {
    tooltip: Tooltip,
  },

  components: {
    AddressItem,
    TabView,
    TabPanel,
    Flag,
    LanguageIcons,
    List,
    OrderItem,
    CartItem,
    ContactSourceProfiles,
    SpmBadge,
    SpmButton,
    GdprEraseAction,
  },

  setup() {
    const { t, locale } = useI18n();
    const store = useStore();

    const selectedData = computed(() => store.getters['dataExplorer/getSelectedData']);

    // Nombre de fiches client affichées dans « Points de vente et identifiants »,
    // remis à zéro quand la fiche passe à un autre contact.
    const sourceProfilesCount = ref(0);
    watch(() => selectedData.value?.generalInformation?.data?.idShopContact, () => {
      sourceProfilesCount.value = 0;
    });

    // Data sources lookup
    const sourceLabelsMap = ref<Map<number, string>>(new Map());

    onBeforeMount(async () => {
      const sources = await fetchDataSources();
      const map = new Map<number, string>();
      sources.forEach((s: { label: string; value: number }) => {
        map.set(s.value, s.label);
      });
      sourceLabelsMap.value = map;
    });

    const getSourceLabel = (dataSourceId: number | string | null | undefined): string => {
      if (!dataSourceId) return '';
      return sourceLabelsMap.value.get(Number(dataSourceId)) || '';
    };

    // Contact sources: collect unique sources from orders and carts
    const contactSources = computed(() => {
      const sourceIds = new Set<number>();
      if (selectedData.value?.orders?.data) {
        selectedData.value.orders.data.forEach((o: any) => {
          if (o.dataSourceId) sourceIds.add(Number(o.dataSourceId));
        });
      }
      if (selectedData.value?.carts?.data) {
        selectedData.value.carts.data.forEach((c: any) => {
          if (c.dataSourceId) sourceIds.add(Number(c.dataSourceId));
        });
      }
      return Array.from(sourceIds)
        .map((id) => ({ id, label: sourceLabelsMap.value.get(id) || `#${id}` }))
        .filter((s) => s.label);
    });

    const hasName = computed(() => {
      const first = selectedData.value?.generalInformation?.data?.firstName?.trim();
      const last = selectedData.value?.generalInformation?.data?.lastName?.trim();
      return !!(first || last);
    });

    const initials = computed(() => {
      const first = selectedData.value?.generalInformation?.data?.firstName?.[0] || '';
      const last = selectedData.value?.generalInformation?.data?.lastName?.[0] || '';
      return (first + last).toUpperCase() || '?';
    });

    const gender = computed(() => {
      if (selectedData.value && selectedData.value.generalInformation.data.gender && selectedData.value.generalInformation.data.gender.toString() === '1') {
        return {
          label: t('dataExplorer.contactFile.fields.gender.male'),
          icon: 'fa fa-mars',
        };
      } if (selectedData.value && selectedData.value.generalInformation.data.gender && selectedData.value.generalInformation.data.gender.toString() === '2') {
        return {
          label: t('dataExplorer.contactFile.fields.gender.female'),
          icon: 'fa fa-venus',
        };
      }
      return {
        label: t('dataExplorer.contactFile.fields.gender.unknown'),
        icon: 'fa fa-neuter',
      };
    });
    const hasAccount = computed(() => selectedData.value
      && (selectedData.value.generalInformation.data.type && selectedData.value.generalInformation.data.type & 1) === 1);
    const isAccountActive = computed(() => selectedData.value
      && selectedData.value.generalInformation.data.active && selectedData.value.generalInformation.data.active.toString() === '1');

    // ===== RGPD (SC-3851) =====

    const gdprEraseRef = ref<GdprEraseDialog | null>(null);

    // RGPD (SC-3851) : effacement définitif, disponible pour tous les profils —
    // aligné sur la liste des contacts. Les garde-fous sont ailleurs : un seul
    // contact à la fois, confirmation par mot tapé, et côté serveur refus des
    // clés API et plafonds de cadence.
    const canGdprErase = computed(() => true);

    /**
     * Le compte client a été supprimé sur la boutique : le contact est désactivé
     * chez nous, mais ses données restent (statistiques, commandes). C'est très
     * exactement le point qui a motivé le ticket SC-3851.
     */
    const isAccountDeletedOnShop = computed(() => hasAccount.value && !isAccountActive.value);

    const openGdprErase = () => {
      if (gdprEraseRef.value?.open) {
        gdprEraseRef.value.open({ context: 'row', selectionInfo: null, rowId: null });
      }
    };

    const onGdprEraseConfirm = async (result: GdprEraseResult) => {
      const idShopContact = selectedData.value?.generalInformation?.data?.idShopContact;
      if (!idShopContact) return;

      try {
        const response: { success?: boolean; message?: string } = await nestCall(
          'post',
          NestApiType.CLIENT_API,
          '/list-actions',
          {},
          '',
          {
            action_type: 'gdpr_erase',
            selection_type: 'manual',
            selected_ids: [String(idShopContact)],
            source_list_id: 'my_contacts',
            confirmation: result.confirmation,
          },
          true,
        );

        if (response?.success) {
          showToastSuccess(t('dataExplorer.contactFile.gdprErase.success'));
          // Les données du contact viennent d'être effacées : la fiche ouverte
          // n'a plus rien à montrer, on la referme.
          store.dispatch('dataExplorer/closeData', { dataId: selectedData.value.dataId });
        } else {
          showToastError(t('dataExplorer.contactFile.gdprErase.error'));
        }
      } catch (error) {
        showToastError(t('dataExplorer.contactFile.gdprErase.error'));
      }
    };

    const phoneNumber = computed(() => {
      if (selectedData.value && selectedData.value.generalInformation.data.country && selectedData.value.generalInformation.data.mobile) {
        const phoneNumberObj = parsePhoneNumberFromString(
          selectedData.value.generalInformation.data.mobile,
          selectedData.value.generalInformation.data.country.toUpperCase(),
        );
        if (phoneNumberObj && phoneNumberObj.isValid()) {
          return phoneNumberObj.formatInternational();
        }
        if (selectedData.value.generalInformation.data.mobile) {
          return selectedData.value.generalInformation.data.mobile;
        }
      }
      return '-';
    });

    const isNumber = (val: string) => {
      if (!val) {
        return false;
      }
      return /^\d+(\.\d+)?$/.test(val);
    };

    const handleRetrieveAddresses = (addresses: Array<ContactAddress>) => {
      store.dispatch(
        'dataExplorer/setAdditionnalData',
        {
          dataId: selectedData.value.dataId,
          newLists: addresses,
          dataKey: 'addresses',
        },
      );
    };

    const handleRetrieveGroups = (groups: Array<ContactGroup>) => {
      store.dispatch(
        'dataExplorer/setAdditionnalData',
        {
          dataId: selectedData.value.dataId,
          newLists: groups,
          dataKey: 'groups',
        },
      );
    };

    const handleRetrieveRejects = (rejects: Array<ContactReject>) => {
      store.dispatch(
        'dataExplorer/setAdditionnalData',
        {
          dataId: selectedData.value.dataId,
          newLists: rejects,
          dataKey: 'rejects',
        },
      );
    };

    const handleRetrieveShops = (shops: Array<string>) => {
      store.dispatch(
        'dataExplorer/setAdditionnalData',
        {
          dataId: selectedData.value.dataId,
          newLists: shops,
          dataKey: 'shops',
        },
      );
    };

    const handleRetrieveOrders = (orders: Array<Order>, total: number) => {
      store.dispatch(
        'dataExplorer/setAdditionnalData',
        {
          dataId: selectedData.value.dataId,
          newLists: orders,
          total,
          dataKey: 'orders',
        },
      );
    };

    const handleOrdersNextPage = (currentPage: number) => {
      store.dispatch(
        'dataExplorer/setAdditionnalData',
        {
          dataId: selectedData.value.dataId,
          currentPage,
          dataKey: 'orders',
        },
      );
    };

    const handleRetrieveCarts = (carts: Array<Cart>, total: number) => {
      store.dispatch(
        'dataExplorer/setAdditionnalData',
        {
          dataId: selectedData.value.dataId,
          newLists: carts,
          total,
          dataKey: 'carts',
        },
      );
    };

    const handleRetrieveCustomDefinitions = (customDefinitions: Array<ContactCustomDefinition>) => {
      store.dispatch(
        'dataExplorer/setAdditionnalData',
        {
          dataId: selectedData.value.dataId,
          newLists: customDefinitions,
          dataKey: 'customData',
        },
      );
    };

    const openCustomDefinitionDetails = (customDefinition: ContactCustomDefinition) => {
      const idShopContact = selectedData.value?.generalInformation?.data?.idShopContact;
      if (!idShopContact) {
        return;
      }

      store.dispatch('dataExplorer/openCustomDataFile', {
        customDefinition,
        idShopContact,
      });
    };

    const handleCartsNextPage = (currentPage: number) => {
      store.dispatch(
        'dataExplorer/setAdditionnalData',
        {
          dataId: selectedData.value.dataId,
          currentPage,
          dataKey: 'carts',
        },
      );
    };

    const channelStatuses = computed(() => {
      const data = selectedData.value?.generalInformation?.data || {};
      const presentation: Record<ChannelSubscriptionStatus, { label: string; cssClass: string }> = {
        no_coordinate: { label: t('dataExplorer.contactFile.fields.notSpecified'), cssClass: 'none' },
        unsubscribed: { label: t('dataExplorer.contactFile.fields.newsletter.unsubscribe.value'), cssClass: 'unsubscribed' },
        subscribed: { label: t('dataExplorer.contactFile.fields.newsletter.optin.value'), cssClass: 'subscribed' },
        double_optin: { label: t('dataExplorer.contactFile.fields.newsletter.doubleOptin.value'), cssClass: 'double-optin' },
        simple_optin: { label: t('dataExplorer.contactFile.fields.newsletter.optin.value'), cssClass: 'optin' },
        not_subscribed: { label: t('dataExplorer.contactFile.fields.newsletter.nonOptin.value'), cssClass: 'none' },
      };
      const build = (channel: SubscriptionChannel, hasCoordinate: boolean) => presentation[
        getChannelSubscriptionStatus(channel, hasCoordinate, data.optinData?.[channel], data.unsubData?.[channel])
      ];
      return {
        email: build('email', !!data.email),
        sms: build('sms', !!data.mobile),
        push: build('push', !!data.hasPushToken),
      };
    });

    return {
      t,
      locale,
      selectedData,
      contactSources,
      sourceProfilesCount,
      getSourceLabel,
      hasName,
      initials,
      gender,
      hasAccount,
      isAccountActive,
      phoneNumber,
      channelStatuses,

      gdprEraseRef,
      canGdprErase,
      isAccountDeletedOnShop,
      openGdprErase,
      onGdprEraseConfirm,

      isNumber,
      formatDate,
      getContactAddressesNest,
      getContactCustomDefinitionsNest,
      getContactGroupsNest,
      getContactOrdersNest,
      getContactListNest,
      getContactCartsNest,
      handleRetrieveAddresses,
      handleRetrieveGroups,
      handleRetrieveShops,
      handleRetrieveOrders,
      handleOrdersNextPage,
      handleRetrieveCarts,
      handleCartsNextPage,
      handleRetrieveCustomDefinitions,
      openCustomDefinitionDetails,
    };
  },
});
</script>

<style lang="scss" scoped>
// ===== ANONYMOUS =====
.cf-anonymous {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 4rem 2rem;
  color: #9ca3af;
  font-size: 1.25rem;

  i {
    font-size: 3rem;
    opacity: 0.5;
  }
}

// ===== MAIN CONTAINER =====
.cf {
  padding: 1.25rem;
}

// ===== HERO HEADER =====
.cf-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.25rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid #e5e7eb;

  &__left {
    display: flex;
    align-items: center;
    gap: 0.875rem;
  }

  &__avatar {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, #94c748, #7eb33a);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    span {
      color: #fff;
      font-size: 1.125rem;
      font-weight: 700;
      letter-spacing: 0.03em;
    }
  }

  &__identity {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__name {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
    line-height: 1.2;
    display: flex;
    gap: 0.375rem;
  }

  &__no-name {
    color: #94a3b8;
    font-style: italic;
    font-weight: 500;
  }

  &__meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  &__id {
    font-size: 0.75rem;
    color: #64748b;
    font-weight: 600;
    background: #f1f5f9;
    padding: 0.0625rem 0.4375rem;
    border-radius: 4px;
    font-family: monospace;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.6875rem;
    font-weight: 600;
    padding: 0.125rem 0.5rem;
    border-radius: 20px;

    &--active {
      background: #dcfce7;
      color: #166534;
    }

    &--inactive {
      background: #fee2e2;
      color: #991b1b;
    }
  }

  &__source-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.6875rem;
    font-weight: 600;
    padding: 0.125rem 0.5rem;
    border-radius: 20px;
    background: #e0e7ff;
    color: #3730a3;

    i {
      font-size: 0.5rem;
    }
  }

  &__dates {
    display: flex;
    align-items: center;
    gap: 0.625rem;
  }

  &__date {
    display: flex;
    align-items: center;
    gap: 0.3125rem;
    font-size: 0.6875rem;
    color: #94a3b8;

    i {
      font-size: 0.8125rem;
    }
  }

  &__date-label {
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  &__date-value {
    font-weight: 700;
    color: #475569;
  }

  &__date-sep {
    width: 1px;
    height: 20px;
    background: #d1d5db;
  }
}

// ===== 2-COL LAYOUT =====
// ===== RGPD (SC-3851) : compte supprimé sur la boutique =====
.cf-deleted-notice {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  margin-bottom: 1.25rem;
  padding: 0.75rem 1rem;
  border-left: 3px solid #d97706;
  background: #fffbeb;
  color: #92400e;
  font-size: 0.875rem;
  line-height: 1.45;

  i {
    flex: none;
  }
}

.cf-layout {
  display: grid;
  grid-template-columns: 340px 1fr;
  gap: 1.25rem;
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
}

.cf-left {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.cf-right {
  min-width: 0;
}

// ===== CARD =====
.cf-card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;

  &--full {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  &__header {
    display: flex;
    align-items: center;
    gap: 0.4375rem;
    padding: 0.625rem 1rem;
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;

    &--no-border {
      border-bottom: none;
    }

    i {
      color: #94c748;
      font-size: 0.875rem;
    }

    h3 {
      font-size: 0.75rem;
      font-weight: 700;
      color: #374151;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      margin: 0;
    }
  }

  &__body {
    padding: 0.5rem 0;

    &--no-pad {
      padding: 0;
    }
  }
}

// ===== INFO ROWS =====
.cf-info-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  transition: background 0.1s;

  &:hover {
    background: #f9fafb;
  }

  &__icon {
    width: 28px;
    height: 28px;
    border-radius: 7px;
    background: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      font-size: 0.8125rem;
      color: #64748b;
    }
  }

  &__content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.0625rem;
  }

  &__label {
    font-size: 0.6875rem;
    font-weight: 500;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  &__value {
    font-size: 0.8125rem;
    font-weight: 500;
    color: #1e293b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__empty {
    color: #d1d5db;
  }
}

// ===== CHANNELS =====
.cf-channel {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    border-bottom: none;
  }

  &__icon {
    width: 28px;
    height: 28px;
    border-radius: 7px;
    background: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      font-size: 0.8125rem;
      color: #64748b;
    }
  }

  &__name {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #374151;
  }

  &__status {
    margin-left: auto;
    font-size: 0.75rem;
    font-weight: 500;
    color: #9ca3af;
  }

  &--subscribed,
  &--optin {
    .cf-channel__icon { background: #dcfce7; i { color: #16a34a; } }
    .cf-channel__status { color: #16a34a; }
  }

  &--double-optin {
    .cf-channel__icon { background: #dbeafe; i { color: #2563eb; } }
    .cf-channel__status { color: #2563eb; }
  }

  &--unsubscribed {
    .cf-channel__icon { background: #fee2e2; i { color: #dc2626; } }
    .cf-channel__status { color: #dc2626; }
  }

  &--none {
    .cf-channel__icon { background: #f1f5f9; i { color: #94a3b8; } }
    .cf-channel__status { color: #9ca3af; }
  }
}

// ===== TAGS =====
.cf-tags {
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
  padding: 0.25rem 1rem 0.5rem;

  &__empty {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.25rem 1rem 0.5rem;
    color: #9ca3af;
    font-size: 0.8125rem;
    font-style: italic;

    i {
      font-size: 0.875rem;
    }
  }
}

.cf-tag-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  height: 1.5rem;
  padding: 0 0.55rem;
  background: #eef2ff;
  color: #3730a3;
  border: 1px solid #e0e7ff;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.5rem;
  max-width: 100%;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: #e0e7ff;
    border-color: #c7d2fe;
  }

  i {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 0.75rem;
    height: 0.75rem;
    font-size: 0.625rem;
    line-height: 1;
    color: #6366f1;
    flex-shrink: 0;
  }

  &__label {
    display: inline-block;
    line-height: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

// ===== LIST ITEMS =====
.cf-list-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;

  &__icon {
    color: #94c748;
    font-size: 0.9375rem;
  }

  &__left {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
  }

  &__meta {
    display: flex;
    flex-direction: column;
    min-width: 0;

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    small {
      color: #6b7280;
      font-size: 0.75rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

.cf-list-item--with-action {
  justify-content: space-between;
  width: 100%;
}

// ===== TABS =====
:deep(.p-tabview) {
  flex: 1;
  display: flex;
  flex-direction: column;

  .p-tabview-nav {
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
    padding: 0 0.5rem;
    gap: 0;
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: thin;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      height: 5px;
    }

    &::-webkit-scrollbar-thumb {
      background: #d1d5db;
      border-radius: 999px;
    }
  }

  .p-tabview-nav li {
    flex: 0 0 auto;
  }

  .p-tabview-nav-link {
    background: transparent;
    border: none;
    border-bottom: 2px solid transparent;
    border-right: none !important;
    color: #6b7280;
    font-size: 0.8125rem;
    font-weight: 500;
    padding: 0.625rem 0.875rem;
    border-radius: 0;
    transition: all 0.15s;
    cursor: pointer;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    min-width: max-content;

    &:hover {
      color: #374151;
      background: rgba(148, 200, 64, 0.04);
    }

    i {
      color: #9ca3af;
      transition: color 0.15s;
    }
  }

  .p-tabview-nav-link:not(.p-disabled):focus {
    box-shadow: none;
  }

  .p-highlight .p-tabview-nav-link {
    background: #fff;
    color: #1e293b;
    font-weight: 600;
    border-bottom-color: #94c748;
    box-shadow: none;

    i {
      color: #94c748;
    }
  }

  .p-tabview-panels {
    background: #fff;
    border: none;
    padding: 1rem;
    flex: 1;
  }

  @media (max-width: 1100px) {
    .p-tabview-nav {
      padding: 0 0.25rem;
    }

    .p-tabview-nav-link {
      font-size: 0.75rem;
      padding: 0.5625rem 0.625rem;
    }
  }
}

// ===== ADDRESSES GRID =====
#addresses-grid-container {
  :deep(ul) {
    display: grid !important;
    grid-template-columns: repeat(2, 1fr) !important;
    gap: 0.75rem !important;
    list-style: none !important;
    padding: 0 !important;
    margin: 0 !important;

    @media (max-width: 900px) {
      grid-template-columns: 1fr !important;
    }
  }

  :deep(li) {
    display: flex !important;
    flex-direction: column !important;
    align-items: stretch !important;
    justify-content: flex-start !important;
    margin-bottom: 0 !important;
  }
}
</style>
